import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Form } from 'antd'
import { ItemTypes, MENU_PARENT, pageBackground } from '../../constants/constants'
import { useDrag, useDrop } from 'react-dnd'
import { Editor } from 'draft-js'
import ReactDOM from 'react-dom';
import { getAncestorFromType, handleBeforeInput, getBlockStyle } from '../RichEditor/utils'
import ContextualMenu from '../ContextualMenu/ContextualMenu'
import { EditorProvider } from '../../contexts/EditorContext'
import FloatyTextStyling from '../Table/FloatyTextStyling'
import { useStore } from '../../store/store'
import { useStyleMap } from '../../hooks'

const LabelRow = memo(function LabelRow({
  item,
  index,
  onBlur,
  checkEmptyLabel,
  box,
  isLast,
  form,
  moveRow,
  onDrop,
  arrayKey,
  updateBox
}) {
  const [focused, setFocused] = useState(false);
  const { label } = item;
  const isDisabled = box.from_linked_section
  const canDeleteLabel = box[arrayKey].length > 1 && !isDisabled;
  const labelForm = Form.useWatch([arrayKey, index, "label"])
  const editorRef = useRef()
  const configuration = useStore((state) => state.configuration)
  const styleMap = useStyleMap()

  const setEditorState = useCallback((fn) => {
    if (!form || isDisabled) return;
    const newEditorState = typeof fn === 'function' ? fn(form.getFieldValue([arrayKey, index, "label"])) : fn;
    form.setFieldValue([arrayKey, index, "label"], newEditorState)
    onBlur()
  }, [arrayKey, form, index, onBlur, isDisabled])

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: "label",
      item: { id: item.id },
      collect: (monitor) => ({
        isDragging: item.id === monitor.getItem()?.id,
      }),
      end: () => onDrop(),
    }),
    [item.id, onDrop]
  );

  const [, drop] = useDrop(
    () => ({
      accept: "label",
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover({ id: draggedId }) {
        if (draggedId !== item.id) {
          moveRow({ draggedId, overId: item.id })
        }
      },
    }),
    [moveRow]
  );

  const deleteLabel = () => {
    if (canDeleteLabel)
      updateBox(box.id, (box) => {
        box[arrayKey] = box[arrayKey].filter((c) => c.id !== item.id);
        if (box.maxNumberOfAnwser > box[arrayKey].length) {
          box.maxNumberOfAnwser = box[arrayKey].length
          form.setFieldValue("maxNumberOfAnwser", box.maxNumberOfAnwser)
        }
        if (box.minNumberOfAnwser > box[arrayKey].length) {
          box.minNumberOfAnwser = box[arrayKey].length
          form.setFieldValue("minNumberOfAnwser", box.minNumberOfAnwser)
        }
      })
  }

  const opacity = isDragging ? 0.8 : 1;

  const getParent = () => {
    if (box.type === ItemTypes.CHECKBOXS_VARIABLE || box.type === ItemTypes.QUESTION) return MENU_PARENT.checkbox
    return MENU_PARENT.option
  }

  const menuAncestorIds = useMemo(() => [box.id, item.id], [box.id, item.id])

  return (
    <div ref={drop}>
      <div style={{
        display: 'flex',
        backgroundColor: 'white',
        alignItems: 'center',
        columnGap: 8,
        ...(!isLast && { borderBottom: '0.5px solid gray' }),
        padding: '2px 8px',
        opacity
      }}
        data-role={pageBackground}
        ref={preview}
      >
        <div ref={isDisabled ? undefined : drag}>
          <FontAwesomeIcon
            icon={icon({ name: 'arrows-up-down-left-right', style: 'solid' })}
            style={{
              cursor: isDisabled ? 'not-allowed' : 'grab',
              color: isDisabled ? '#b2b2b2' : 'black'
            }}
          />
        </div>
        <EditorProvider setEditorState={setEditorState} editorState={label}>
          <Form.Item
            className={`variable-editor__input ${focused ? 'focused' : ''} ${isDisabled ? 'disabled' : ''}`}
            name={[arrayKey, index, "label"]}
            initialValue={label}
            preserve={false}
            onBlur={() => {
              onBlur()
            }}
            style={{
              marginBottom: 0,
              flex: 1,
              overflow: 'hidden',
            }}
            rules={[{ required: true }]}
            help=""
            valuePropName='editorState'
          >
            <Editor
              readOnly={isDisabled}
              stripPastedStyles={true}
              ref={editorRef}
              customStyleMap={styleMap}
              blockStyleFn={getBlockStyle}
              handleBeforeInput={(chars, editorState) => handleBeforeInput(
                {
                  chars,
                  editorState,
                  setEditorState: (newEditorState) => form.setFieldValue([arrayKey, index, "label"], newEditorState)
                }
              )}
              data-role={pageBackground}
              onFocus={() => setFocused(true)}
              onChange={checkEmptyLabel}
              onBlur={(e) => {
                if (!e.relatedTarget ||
                  (e.relatedTarget.className !== 'text-styling-component' &&
                    e.relatedTarget.className !== 'ant-select-selection-search-input')) {
                  setFocused(false)
                }
              }} />
          </Form.Item>
        </EditorProvider>
        <FontAwesomeIcon
          icon={icon({ name: 'trash', style: 'light' })}
          style={{
            color: canDeleteLabel ? 'red' : 'gray',
            cursor: canDeleteLabel ? 'pointer' : 'not-allowed'
          }}
          onClick={deleteLabel}
        />
      </div>
      {!!labelForm && ReactDOM.createPortal(
        <ContextualMenu
          editorState={labelForm}
          setEditorState={setEditorState}
          menuAncestorType={getAncestorFromType(box.type, getParent())}
          menuAncestorIds={menuAncestorIds}
        />,
        document.getElementById('SelectionVariablePortal')
      )}
      {focused && !!labelForm && ReactDOM.createPortal(
        <FloatyTextStyling
          editorState={labelForm}
          setEditorState={setEditorState}
          colors={configuration.colors}
          hasAlignement={false}
          hasList={true}
          hasForm={false}
          hasFontSize={true}
        />,
        document.getElementById('SelectionVariablePortal')
      )}
    </div>
  )
})

export default LabelRow
